import { showCategoryModal } from '@/components/cashflow/categories/CategoryModal';
import CategoryIcon from '@/components/cashflow/CategoryIcon';
import { RenderCellProps } from '@/components/cashflow/transactions/TableRow';
import useCategoryOptions from '@/components/cashflow/useCategoryOptions';
import useUpdateTransactionEntry from '@/components/cashflow/useUpdateTransactionEntry';
import { StringOption } from '@/components/common/FancySelectInput';
import { FlexRow } from '@/components/common/Flex';
import InputField from '@/components/common/InputField';
import { groupHoverStyleProps } from '@/components/common/styleHelpers';
import { Text } from '@/components/common/Typography';
import { extractTextFromElement } from '@/util/react';
import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

const CategoryCell = ({ transactionEntry, onEdit }: RenderCellProps) => {
    // Storing categoryName in state, so we can optimistically update it when the user selects a new category
    // Otherwise, there's a quick flash to the old category name before the new one is saved
    const [categoryName, setCategoryName] = useState<string>(
        transactionEntry.category.name
    );
    const categoryOptions = useCategoryOptions();
    const [menuIsOpen, setMenuOpen] = useState<boolean>(false);
    const [isEditing, setEditing] = useState<boolean>(false);
    const updateTransactionEntry = useUpdateTransactionEntry(transactionEntry);
    const ref = useRef<HTMLSelectElement>(null);

    const onChange = async (option: StringOption) => {
        const previousCategoryName = categoryName;
        const newCategoryName = extractTextFromElement(option.label);
        setCategoryName(newCategoryName);

        try {
            await updateTransactionEntry({
                variables: {
                    transactionEntryId: transactionEntry.id,
                    categoryId: option.value,
                },
            });
        } catch {
            // Roll back optimistic update
            setCategoryName(previousCategoryName);
        }
    };

    useEffect(() => {
        if (isEditing && ref.current) {
            ref.current.focus();
        }
    }, [isEditing]);

    return (
        <FlexRow justifyContent="space-between" m={-2}>
            {isEditing ? (
                <InputField
                    type="fancySelect"
                    name="category"
                    ref={ref}
                    w="100%"
                    fancySelectOptions={{
                        isClearable: false,
                        menuIsOpen,
                        onMenuOpen: () => setMenuOpen(true),
                        onMenuClose: () => {
                            setMenuOpen(false);
                            onEdit(false);
                            setEditing(false);
                        },
                        fullScreenPlacement: 'left',
                        menuWidth: 300,
                        cta: {
                            content: 'Create new subcategory',
                            iconName: 'Plus',
                            onClick: () => {
                                showCategoryModal({
                                    onSuccess: async ({ id, name }) => {
                                        await onChange({
                                            label: name,
                                            value: id,
                                        });
                                    },
                                });
                            },
                        },
                    }}
                    value={transactionEntry.category.id}
                    onChange={onChange}
                    options={categoryOptions}
                />
            ) : (
                <FlexRow
                    alignItems="center"
                    p={2}
                    w="100%"
                    overflow="hidden"
                    borderRadius={6}
                    border="1px solid transparent"
                    opacity={transactionEntry.isPending ? 0.5 : 1}
                    {...(!transactionEntry.isPending &&
                        groupHoverStyleProps({
                            borderColor: 'gray.300',
                        }))}
                    onClick={(e) => {
                        if (transactionEntry.isPending) return;

                        e.stopPropagation();
                        setEditing(!isEditing);
                        onEdit(!isEditing);
                        setMenuOpen(!isEditing);
                    }}
                >
                    <Box ml={1}>
                        <CategoryIcon category={transactionEntry.category} />
                    </Box>
                    <Text
                        ml={2}
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                    >
                        {categoryName}
                    </Text>
                </FlexRow>
            )}
        </FlexRow>
    );
};

export default CategoryCell;
